import React, { useRef } from 'react';
import { ArrowLeft, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { periodLabel } from '../gameUtils';
import { boxScoreFileName, buildShareText } from './boxScoreShare';
import { useBoxScoreShare } from './useBoxScoreShare';
import { BoxScoreTeamTable } from './BoxScoreTeamTable';
import { BoxScoreLandscape } from './BoxScoreLandscape';
import { BoxScoreBanner, BoxScoreShareRow } from './BoxScoreParts';

const buildMeta = (props) => {
  const { game } = props;
  const title = `${game.team_a.name} vs ${game.team_b.name} — Box Score`;
  const text = buildShareText(props);
  return {
    title,
    fileName: boxScoreFileName(game, props.quarter),
    whatsappHref: `https://wa.me/?text=${encodeURIComponent(text)}`,
    mailHref: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text)}`,
  };
};

// Live box score from Game Settings with image sharing (portrait in-modal, landscape off-screen).
export function BoxScoreModal(props) {
  const { game, boxscore, teamTotals, quarter, scoreA, scoreB, onClose } = props;
  const captureRef = useRef(null);
  const landscapeRef = useRef(null);
  const ready = game && boxscore && teamTotals;
  const share = useBoxScoreShare({ captureRef, landscapeRef, meta: ready ? buildMeta(props) : null });
  if (!ready) return null;
  const period = periodLabel(quarter);

  return (
    <div className="fixed inset-0 z-[110] bg-[#0A0F2C]/95 backdrop-blur-sm flex flex-col" data-testid="boxscore-modal">
      <div className="flex items-center justify-between px-4 py-3 bg-[#00247D] border-b border-white/10 flex-shrink-0">
        <button onClick={onClose} className="text-white/80 hover:text-white text-sm flex items-center gap-1.5" data-testid="boxscore-close-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="text-center">
          <div className="text-white text-base sm:text-lg font-semibold leading-tight">Live Box Score</div>
          <div className="text-amber-300 text-[11px] sm:text-xs font-bold tracking-wider">{period}</div>
        </div>
        <Button size="sm"
          onClick={share.handleShare}
          disabled={share.generating}
          className="bg-amber-400 hover:bg-amber-300 text-[#00247D] font-black h-8 px-3 disabled:opacity-60"
          data-testid="boxscore-share-btn">
          {share.generating
            ? <><span className="inline-block w-3.5 h-3.5 mr-1.5 border-2 border-[#00247D] border-t-transparent rounded-full animate-spin" /> Generating</>
            : <><Share2 className="w-3.5 h-3.5 mr-1.5" /> Share Image</>}
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto" data-testid="boxscore-scroll-region">
        {/* Everything inside the capture div is what ends up in the portrait PNG. */}
        <div ref={captureRef} className="bg-[#0A0F2C]" data-testid="boxscore-capture">
          <BoxScoreBanner game={game} scoreA={scoreA} scoreB={scoreB} period={period} />
          <div className="px-3 sm:px-5 py-4 space-y-4">
            {['team_a', 'team_b'].map(tk => (
              <BoxScoreTeamTable key={tk} teamKey={tk} team={game[tk]} rows={boxscore[tk]} totals={teamTotals[tk]} />
            ))}
            <div className="text-center text-[10px] uppercase tracking-widest text-white/30 pt-1">
              via Schedule Guru
            </div>
          </div>
        </div>
        <BoxScoreShareRow
          generating={share.generating}
          onWhatsApp={share.handleWhatsApp}
          onMail={share.handleMail}
          onDownload={share.handleDownload}
          onShareLandscape={share.handleShareLandscape}
        />
      </div>

      <BoxScoreLandscape
        ref={landscapeRef}
        game={game}
        boxscore={boxscore}
        teamTotals={teamTotals}
        scoreA={scoreA}
        scoreB={scoreB}
        period={period}
      />
    </div>
  );
}
